import React, { useState } from 'react';
import {
  IAuditoriaAdiantamentoResultado,
  IItemAdiantamento,
} from '../interfaces/IAuditoriaAdiantamento';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaAdiantamentoResultado;
}



const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'PENDENTE_VALIDACAO':
      return 'bi-exclamation-diamond-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    default:
      return 'bi-check-circle-fill';
  }
};

const getLabelTipoOcorrencia = (tipo: string) => {
  switch (tipo) {
    case 'ADIANTAMENTO_PENDENTE':
      return { label: 'Pendente Validação', classe: 'auditoria-status status-PENDENTE_VALIDACAO' };
    case 'ADIANTAMENTO_VENCIDO':
      return { label: 'Vencido', classe: 'auditoria-status status-ALERTA' };
    case 'ADIANTAMENTO_EM_ABERTO':
      return { label: 'Em Aberto', classe: 'auditoria-status status-AVISO' };
    default:
      return { label: 'Baixado', classe: 'auditoria-status status-CONCILIADO' };
  }
};

const getLabelTipoOperacao = (tipo: string) => {
  switch (tipo) {
    case 'ESTRANGEIRO':
      return { label: 'Fornecedor Estrangeiro / Importação', prazo: 180, classe: 'auditoria-status status-AVISO' };
    case 'IMOBILIZADO':
      return { label: 'Aquisição de Imobilizado', prazo: 365, classe: 'auditoria-status status-CONCILIADO' };
    case 'OBRA_PROJETO':
      return { label: 'Obra / Projeto', prazo: 365, classe: 'auditoria-status status-CONCILIADO' };
    default:
      return { label: 'Fornecedor Nacional (Padrão)', prazo: 60, classe: 'auditoria-status status-CONCILIADO' };
  }
};

const getLabelStatusBanner = (status: string) => {
  if (status === 'PENDENTE_VALIDACAO') return 'PENDENTE DE VALIDAÇÃO';
  return status;
};

// COR-277: drill-down a nível de lançamento — todo ALERTA/PENDENTE_VALIDACAO precisa mostrar não
// só o saldo agregado, mas os lançamentos reais (débitos + créditos) que o formam.
const LIMITE_LANCAMENTOS_VISIVEIS = 10;

const BlocoLancamentosRelacionados: React.FC<{ item: IItemAdiantamento }> = ({ item }) => {
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const lancamentos = item.lancamentosRelacionados || [];

  if (lancamentos.length === 0) return null;

  const visiveis = mostrarTodos ? lancamentos : lancamentos.slice(0, LIMITE_LANCAMENTOS_VISIVEIS);

  return (
    <div className='auditoria-card' style={{ margin: '8px 0', padding: '10px 14px', background: '#fff' }}>
      <strong style={{ fontSize: 12 }}>Lançamentos Relacionados ({lancamentos.length})</strong>
      <div className='table-responsive mt-1'>
        <table className='auditoria-table' style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Data</th>
              <th>Nº Lançamento</th>
              <th>Histórico</th>
              <th className='text-end'>Débito</th>
              <th className='text-end'>Crédito</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((lanc, idx) => (
              <tr key={`${lanc.numeroLancamento}-${lanc.data}-${idx}`}>
                <td>{formatDate(lanc.data)}</td>
                <td><code>{lanc.numeroLancamento || '-'}</code></td>
                <td>{lanc.historico}</td>
                <td className='text-end'>{lanc.valorDebito > 0 ? formatCurrency(lanc.valorDebito) : '-'}</td>
                <td className='text-end'>{lanc.valorCredito > 0 ? formatCurrency(lanc.valorCredito) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {lancamentos.length > LIMITE_LANCAMENTOS_VISIVEIS && !mostrarTodos && (
        <button
          type='button'
          className='btn btn-link btn-sm p-0 mt-1'
          style={{ fontSize: 12 }}
          onClick={() => setMostrarTodos(true)}
        >
          + {lancamentos.length - LIMITE_LANCAMENTOS_VISIVEIS} lançamento(s) adicional(is)
        </button>
      )}
    </div>
  );
};

const AuditoriaAdiantamentoDetalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);

  const adiantamentosEstagnados = resultado.adiantamentosEstagnados || [];
  const adiantamentosConciliados = resultado.adiantamentosConciliados || [];

  // COR-37: separar por nível de urgência
  const itensPendentes = adiantamentosEstagnados.filter(x => x.status === 'PENDENTE_VALIDACAO');
  const itensAlerta    = adiantamentosEstagnados.filter(x => x.status === 'ALERTA');

  const alternarExpandir = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
  };

  const renderTabelaItens = (itens: IItemAdiantamento[], prefixo: string) => (
    <div className='table-responsive mt-4'>
      <table className='auditoria-table'>
        <thead>
          <tr>
            <th>Conta</th>
            <th title='Data da última movimentação com saldo positivo (débito ou crédito parcial). Se houve baixa parcial, o aging conta a partir desta data, não do débito original.'>
              Última Movimentação
            </th>
            <th className='text-center' title='Dias desde a última movimentação com saldo até o fim do período'>
              Dias sem Baixa
            </th>
            <th className='text-end' title='Saldo remanescente após baixas parciais aplicadas'>
              Saldo Remanescente
            </th>
            <th title='Tipo de operação classificado por referencial RFB e descrição da conta. Define o prazo aplicado ao aging.'>
              Tipo de Operação
            </th>
            <th>Ocorrência</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {itens.map((item: IItemAdiantamento, idx) => {
            const chave = `${prefixo}-${item.empresaId}-${item.contaContabil}-${idx}`;
            const expandido = itemExpandido === chave;
            const { label: labelTipo, classe: classeTipo } = getLabelTipoOcorrencia(item.tipoOcorrencia);
            const { label: labelOperacao, classe: classeOperacao } = getLabelTipoOperacao(item.tipoOperacao);
            const temBaixaParcial = item.totalBaixasAplicadas > 0;
            const ePendente = item.status === 'PENDENTE_VALIDACAO';

            return (
              <React.Fragment key={chave}>
                <tr
                  className='auditoria-row-tributo'
                  style={ePendente ? { background: '#fff8f4' } : undefined}
                  onClick={() => alternarExpandir(chave)}
                >
                  <td>
                    <strong>{item.descricaoConta || item.contaContabil}</strong>
                    {item.contaReferencial && (
                      <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>
                    )}
                  </td>
                  <td>
                    {formatDate(item.dataUltimaMovimentacao)}
                    {temBaixaParcial && (
                      <div
                        className='text-muted'
                        style={{ fontSize: 10 }}
                        title='Aging contado da última baixa parcial, não do débito original'
                      >
                        após baixa parcial
                      </div>
                    )}
                  </td>
                  <td className='text-center'>
                    <span className={`auditoria-status status-${item.status}`}>{item.diasEstagnado} dias</span>
                  </td>
                  <td className='text-end auditoria-col-highlight'>{formatCurrency(item.saldoEstagnado)}</td>
                  <td>
                    <span className={classeOperacao} style={{ fontSize: '0.72rem' }}>{labelOperacao}</span>
                    <div className='text-muted' style={{ fontSize: 10 }}>{item.limiarDiasAplicado} dias</div>
                  </td>
                  <td>
                    <span className={classeTipo} style={{ fontSize: '0.75rem' }}>{labelTipo}</span>
                  </td>
                  <td>
                    <span className={`auditoria-status status-${item.status}`}>
                      <i className={`bi ${getIconeStatus(item.status)} me-1`} />
                      {ePendente ? 'PENDENTE' : item.status}
                    </span>
                    <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                  </td>
                </tr>

                {expandido && (
                  <tr>
                    <td colSpan={7} style={{ background: ePendente ? '#fff8f4' : '#fafbfc' }}>
                      <div className='py-2 px-3'>
                        <div className='mb-2'>
                          <i
                            className={`bi ${getIconeStatus(item.status)} me-1`}
                            style={{ color: ePendente ? '#a84000' : undefined }}
                          />
                          <strong>Diagnóstico:</strong> {item.mensagemDiagnostico}
                        </div>

                        <div className='auditoria-card' style={{ margin: '8px 0', padding: '10px 14px', background: '#f8f9fa' }}>
                          <strong style={{ fontSize: 12 }}>Memória de cálculo</strong>
                          <table style={{ fontSize: 12, width: '100%', marginTop: 6, borderCollapse: 'collapse' }}>
                            <tbody>
                              {item.dataPrimeiroDebito && (
                                <tr>
                                  <td style={{ color: '#6c757d', width: 200 }}>Data do adiantamento original</td>
                                  <td>{formatDate(item.dataPrimeiroDebito)}</td>
                                </tr>
                              )}
                              <tr>
                                <td style={{ color: '#6c757d' }}>Valor original do adiantamento</td>
                                <td>{formatCurrency(item.valorOriginalAdiantamento)}</td>
                              </tr>
                              <tr>
                                <td style={{ color: '#6c757d' }}>Baixas / créditos aplicados</td>
                                <td>
                                  {formatCurrency(item.totalBaixasAplicadas)}
                                  {!temBaixaParcial && (
                                    <span className='text-muted ms-1'>(nenhuma baixa registrada)</span>
                                  )}
                                </td>
                              </tr>
                              <tr>
                                <td style={{ color: '#6c757d' }}>Saldo remanescente</td>
                                <td><strong>{formatCurrency(item.saldoEstagnado)}</strong></td>
                              </tr>
                              <tr>
                                <td style={{ color: '#6c757d' }}>Última movimentação com saldo</td>
                                <td>
                                  {formatDate(item.dataUltimaMovimentacao)}
                                  {temBaixaParcial && (
                                    <span className='text-muted ms-1' style={{ fontSize: 11 }}>
                                      (data da última baixa parcial — aging conta daqui)
                                    </span>
                                  )}
                                </td>
                              </tr>
                              <tr>
                                <td style={{ color: '#6c757d' }}>Dias em aberto</td>
                                <td><strong>{item.diasEstagnado} dias</strong></td>
                              </tr>
                              <tr style={{ borderTop: '1px solid #dee2e6' }}>
                                <td style={{ color: '#6c757d' }}>Tipo de operação</td>
                                <td>
                                  <span className={classeOperacao} style={{ fontSize: '0.72rem' }}>{labelOperacao}</span>
                                </td>
                              </tr>
                              <tr>
                                <td style={{ color: '#6c757d' }}>Prazo aplicado ao aging</td>
                                <td>
                                  <strong>{item.limiarDiasAplicado} dias</strong>
                                  {ePendente ? (
                                    <span style={{ color: '#a84000', fontWeight: 700, marginLeft: 8, fontSize: 11 }}>
                                      (acima de {item.limiarDiasAplicado * 2} dias — PENDENTE DE VALIDAÇÃO)
                                    </span>
                                  ) : (
                                    <span className='text-muted ms-2' style={{ fontSize: 11 }}>
                                      (base para classificação como ALERTA)
                                    </span>
                                  )}
                                </td>
                              </tr>
                              <tr style={{ borderTop: '1px solid #dee2e6' }}>
                                <td style={{ color: '#6c757d' }}>Referencial RFB (I051)</td>
                                <td>
                                  {item.contaReferencial
                                    ? <code style={{ fontSize: 12 }}>{item.contaReferencial}</code>
                                    : <span className='text-muted' style={{ fontSize: 11 }}>Não mapeado no ECD</span>}
                                </td>
                              </tr>
                              <tr>
                                <td style={{ color: '#6c757d' }}>Fonte de classificação</td>
                                <td>
                                  {item.fonteClassificacao === 'REFERENCIAL_RFB'
                                    ? (
                                      <span className='auditoria-status status-CONCILIADO' style={{ fontSize: '0.72rem' }}>
                                        Referencial RFB (I051)
                                      </span>
                                    ) : (
                                      <span className='auditoria-status status-AVISO' style={{ fontSize: '0.72rem' }}>
                                        Nome da conta (I051 ausente ou não mapeado)
                                      </span>
                                    )}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        <BlocoLancamentosRelacionados item={item} />

                        <div className='mt-2 text-muted' style={{ fontSize: 12 }}>
                          <i className='bi bi-info-circle me-1' />
                          {ePendente
                            ? 'Documentação urgente: contrato com prazo superior, nota fiscal, baixa em período subsequente ou reclassificação. Impacto fiscal calculado apenas após confirmação de erro por validação humana.'
                            : 'Verificar antes de concluir: contrato com prazo superior, importação, obra/ativo em andamento, fornecedor estrangeiro, nota fiscal ou baixa em período subsequente.'}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>
            Auditoria de Competência de Adiantamentos — {getLabelStatusBanner(resultado.status)}
          </strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        <div
          className={`auditoria-kpi ${resultado.impactoFinanceiroTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}
        >
          <div className='auditoria-kpi-label'>Exposição Financeira</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFinanceiroTotal)}</div>
          {resultado.impactoFinanceiroTotal === 0 ? (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          ) : (
            <div className='auditoria-kpi-help'>
              Soma dos saldos de adiantamentos vencidos — indica exposição financeira não resolvida.
              Penalidade fiscal (Art. 290 RIR/2018 + Art. 12 Lei 8.218/1991) só se aplica após confirmação de despesa omitida.
            </div>
          )}
        </div>

        <div className={`auditoria-kpi ${resultado.totalPendentes > 0 ? 'auditoria-kpi-alerta' : ''}`}>
          <div className='auditoria-kpi-label'>Pendentes de Validação</div>
          <div className='auditoria-kpi-value'>
            <span className='auditoria-contador auditoria-contador-pendente'>{resultado.totalPendentes}</span>
          </div>
          <div className='auditoria-kpi-help'>
            Aging acima de 2× o prazo aplicável — possível despesa omitida. Documentação urgente necessária.
          </div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Adiantamentos Vencidos</div>
          <div className='auditoria-kpi-value'>
            <span className='auditoria-contador auditoria-contador-alerta'>{resultado.totalAlertas}</span>
          </div>
          <div className='auditoria-kpi-help'>
            Contas com saldo devedor sem baixa além do prazo parametrizado (padrão: 60 dias).
            Requerem verificação de documentação antes de qualquer conclusão fiscal.
          </div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Base legal e critérios de revisão aplicados.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div><strong>Estagnação de Adiantamentos:</strong> {resultado.fundamentacaoEstagnacao}</div>
        </div>
      )}

      {/* Seção 1: Pendentes de Validação — urgência máxima (aging > 2× prazo) */}
      {itensPendentes.length > 0 && (
        <div className='auditoria-card' style={{ borderColor: '#ffb07a' }}>
          <h3 style={{ color: '#a84000' }}>
            <i className='bi bi-exclamation-diamond-fill me-2' style={{ fontSize: 20 }} />
            Pendentes de Validação — Atenção Urgente ({itensPendentes.length})
          </h3>
          <p className='text-muted'>
            Aging acima de 2× o prazo parametrizado — configura <strong>possível despesa omitida</strong>.
            Documentação urgente necessária antes de qualquer conclusão fiscal.
            Impacto fiscal (Art. 290 RIR/2018 + Art. 12 Lei 8.218/1991) calculado apenas após confirmação de erro por validação humana.
          </p>
          {renderTabelaItens(itensPendentes, 'pv')}
        </div>
      )}

      {/* Seção 2: Adiantamentos Vencidos — revisão recomendada (limiar < aging ≤ 2× limiar) */}
      <div className='auditoria-card'>
        <h3>
          {itensAlerta.length > 0 && (
            <i className='bi bi-exclamation-triangle-fill text-warning me-2' style={{ fontSize: 18 }} />
          )}
          Adiantamentos Vencidos — Revisão Recomendada ({itensAlerta.length})
        </h3>
        <p className='text-muted'>
          Contas de adiantamento com saldo devedor além do prazo parametrizado.
          Aging elevado é um <strong>indicador de revisão</strong> — verificar documentação, contratos, notas fiscais
          ou baixas em período subsequente antes de concluir por despesa omitida.
        </p>

        {itensAlerta.length === 0 && (
          <div className='text-muted mt-4'>Nenhum adiantamento no nível de alerta identificado no período.</div>
        )}

        {itensAlerta.length > 0 && renderTabelaItens(itensAlerta, 'ae')}
      </div>

      {/* Seção 3: Adiantamentos Conciliados — visibilidade de que a auditoria rodou */}
      {adiantamentosConciliados.length > 0 && (
        <div className='auditoria-card'>
          <h3>
            <i className='bi bi-check-circle-fill text-success me-2' style={{ fontSize: 18 }} />
            Adiantamentos Verificados e Conciliados
          </h3>
          <p className='text-muted'>
            Contas de adiantamento que foram auditadas e estão em conformidade — saldo zerado ou dentro do prazo para baixa.
          </p>

          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th title='Data da última movimentação com saldo positivo'>Última Movimentação</th>
                  <th className='text-center'>Dias</th>
                  <th className='text-end'>Saldo</th>
                  <th>Tipo</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {adiantamentosConciliados.map((item: IItemAdiantamento, idx) => {
                  const chave = `ac-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;
                  const { label: labelTipo, classe: classeTipo } = getLabelTipoOcorrencia(item.tipoOcorrencia);

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && (
                            <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>
                          )}
                        </td>
                        <td>{item.dataUltimaMovimentacao ? formatDate(item.dataUltimaMovimentacao) : '-'}</td>
                        <td className='text-center'>{item.diasEstagnado || '-'}</td>
                        <td className='text-end'>{formatCurrency(item.saldoEstagnado)}</td>
                        <td><span className={classeTipo} style={{ fontSize: '0.75rem' }}>{labelTipo}</span></td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={6} style={{ background: '#f0fdf4' }}>
                            <div className='py-2 px-3'>
                              <div className='mb-2'>
                                <i className='bi bi-check-circle-fill text-success me-1' />
                                <strong>Diagnóstico:</strong> {item.mensagemDiagnostico}
                              </div>

                              <div
                                className='auditoria-card'
                                style={{ margin: '8px 0', padding: '10px 14px', background: '#f0fdf4', borderColor: '#b7ebc6' }}
                              >
                                <strong style={{ fontSize: 12 }}>Memória de cálculo</strong>
                                <table style={{ fontSize: 12, width: '100%', marginTop: 6, borderCollapse: 'collapse' }}>
                                  <tbody>
                                    {item.dataPrimeiroDebito && (
                                      <tr>
                                        <td style={{ color: '#6c757d', width: 220 }}>Data do adiantamento original</td>
                                        <td>{formatDate(item.dataPrimeiroDebito)}</td>
                                      </tr>
                                    )}
                                    {item.valorOriginalAdiantamento > 0 && (
                                      <tr>
                                        <td style={{ color: '#6c757d' }}>Valor original do adiantamento</td>
                                        <td>{formatCurrency(item.valorOriginalAdiantamento)}</td>
                                      </tr>
                                    )}
                                    <tr>
                                      <td style={{ color: '#6c757d' }}>Baixas / créditos aplicados</td>
                                      <td>
                                        {formatCurrency(item.totalBaixasAplicadas)}
                                        {item.tipoOcorrencia === 'ADIANTAMENTO_BAIXADO' && (
                                          <span className='text-success ms-2' style={{ fontSize: 11, fontWeight: 700 }}>
                                            ✓ liquidação total
                                          </span>
                                        )}
                                        {item.totalBaixasAplicadas === 0 && item.tipoOcorrencia === 'ADIANTAMENTO_EM_ABERTO' && (
                                          <span className='text-muted ms-1'>(nenhuma baixa registrada no período)</span>
                                        )}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td style={{ color: '#6c757d' }}>Saldo remanescente</td>
                                      <td>
                                        <strong style={{ color: item.saldoEstagnado === 0 ? '#126b3f' : '#1f2a44' }}>
                                          {formatCurrency(item.saldoEstagnado)}
                                        </strong>
                                        {item.saldoEstagnado === 0 && (
                                          <span className='text-success ms-2' style={{ fontSize: 11 }}>✓ zerado</span>
                                        )}
                                      </td>
                                    </tr>
                                    {item.dataUltimaMovimentacao && (
                                      <tr>
                                        <td style={{ color: '#6c757d' }}>
                                          {item.tipoOcorrencia === 'ADIANTAMENTO_BAIXADO' ? 'Data da baixa' : 'Última movimentação com saldo'}
                                        </td>
                                        <td>
                                          {formatDate(item.dataUltimaMovimentacao)}
                                          {item.totalBaixasAplicadas > 0 && item.tipoOcorrencia === 'ADIANTAMENTO_EM_ABERTO' && (
                                            <span className='text-muted ms-1' style={{ fontSize: 11 }}>
                                              (aging conta daqui)
                                            </span>
                                          )}
                                        </td>
                                      </tr>
                                    )}
                                    {item.diasEstagnado > 0 && (
                                      <tr>
                                        <td style={{ color: '#6c757d' }}>Dias em aberto</td>
                                        <td><strong>{item.diasEstagnado} dias</strong></td>
                                      </tr>
                                    )}
                                    <tr style={{ borderTop: '1px solid #d1fae5' }}>
                                      <td style={{ color: '#6c757d' }}>Tipo de operação</td>
                                      <td>
                                        <span className={getLabelTipoOperacao(item.tipoOperacao).classe} style={{ fontSize: '0.72rem' }}>
                                          {getLabelTipoOperacao(item.tipoOperacao).label}
                                        </span>
                                      </td>
                                    </tr>
                                    <tr>
                                      <td style={{ color: '#6c757d' }}>Prazo de referência (aging)</td>
                                      <td>
                                        <strong>{item.limiarDiasAplicado} dias</strong>
                                        {item.tipoOcorrencia === 'ADIANTAMENTO_EM_ABERTO' && (
                                          <span className='text-success ms-2' style={{ fontSize: 11, fontWeight: 700 }}>
                                            ({item.diasEstagnado} dias ≤ {item.limiarDiasAplicado} dias — dentro do prazo)
                                          </span>
                                        )}
                                        {item.tipoOcorrencia === 'ADIANTAMENTO_BAIXADO' && (
                                          <span className='text-success ms-2' style={{ fontSize: 11 }}>
                                            (adiantamento já liquidado — prazo não atingido)
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                    <tr style={{ borderTop: '1px solid #d1fae5' }}>
                                      <td style={{ color: '#6c757d' }}>Referencial RFB (I051)</td>
                                      <td>
                                        {item.contaReferencial
                                          ? <code style={{ fontSize: 12 }}>{item.contaReferencial}</code>
                                          : <span className='text-muted' style={{ fontSize: 11 }}>Não mapeado no ECD</span>}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td style={{ color: '#6c757d' }}>Fonte de classificação</td>
                                      <td>
                                        {item.fonteClassificacao === 'REFERENCIAL_RFB'
                                          ? (
                                            <span className='auditoria-status status-CONCILIADO' style={{ fontSize: '0.72rem' }}>
                                              Referencial RFB (I051)
                                            </span>
                                          ) : (
                                            <span className='auditoria-status status-AVISO' style={{ fontSize: '0.72rem' }}>
                                              Nome da conta (I051 ausente ou não mapeado)
                                            </span>
                                          )}
                                      </td>
                                    </tr>
                                    <tr style={{ borderTop: '1px solid #d1fae5', background: '#ecfdf5' }}>
                                      <td style={{ color: '#126b3f', fontWeight: 700 }}>
                                        <i className='bi bi-check-circle-fill me-1' />
                                        Motivo CONCILIADO
                                      </td>
                                      <td style={{ color: '#126b3f', fontWeight: 700 }}>
                                        {item.tipoOcorrencia === 'ADIANTAMENTO_BAIXADO' && (
                                          'Saldo devedor zerado ao final do período — adiantamento liquidado corretamente'
                                        )}
                                        {item.tipoOcorrencia === 'ADIANTAMENTO_EM_ABERTO' && (
                                          `${item.diasEstagnado} dias em aberto ≤ ${item.limiarDiasAplicado} dias — dentro do prazo parametrizado`
                                        )}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default AuditoriaAdiantamentoDetalhe;
